// useListFilters.ts
import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { addMonths } from 'date-fns';
import { getFromStorage, saveToStorage } from '@forvalter/utils/storage';
import { formatDateAPI as formatDate } from '@klp/utils/dateTimeFormatter';

interface ListFilters {
    statuses: Array<string>;
    // Empty means the default, see getDefaultFromDate. null means the user has
    // cleared the field, so the list isn't filtered on date.
    fromDate: string | null;
    prosjektskjemaTypes: Array<string>;
    // The search, as last searched for. The filters aren't used while it is set.
    oppslag?: string;
    // Shows the skjema assigned to the logged in user. The search and the
    // filters aren't used while it is on.
    tildeltMeg?: boolean;
}

const DEFAULT_FILTERS: ListFilters = {
    statuses: [],
    fromDate: '',
    prosjektskjemaTypes: [],
};

// Query parameter that opens a list view with «Tildelt meg» on, e.g. from the
// front page
const TILDELT_MEG_PARAM = 'tildeltMeg';

/** Search part of a link that opens a list view with «Tildelt meg» on. */
export const tildeltMegLenke = `?${TILDELT_MEG_PARAM}=true`;

/** The date the list views filter from when the user hasn't picked one. */
export const getDefaultFromDate = () => addMonths(new Date(), -12);

export const useListFilters = (viewKey: 'prosjekt' | 'leverandor') => {
    const storageKey = `stored${viewKey.charAt(0).toUpperCase() + viewKey.slice(1)}Filters`;

    const [filters, setFilters] = useState<ListFilters>(() => {
        const stored = getFromStorage<ListFilters>(storageKey);
        return stored || DEFAULT_FILTERS;
    });

    // The text in the search field before the debounce, so the filters are
    // disabled and enabled again as soon as the user types
    const [sokTekst, setSokTekst] = useState(filters.oppslag ?? '');

    useEffect(() => {
        saveToStorage(storageKey, filters);
    }, [filters, storageKey]);

    // Turns «Tildelt meg» on when the list is opened with tildeltMegLenke, and
    // removes the parameter so a reload doesn't turn it on again
    const location = useLocation();
    const history = useHistory();
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get(TILDELT_MEG_PARAM) !== 'true') return;

        setFilters((prev) => ({ ...prev, tildeltMeg: true }));
        searchParams.delete(TILDELT_MEG_PARAM);
        history.replace({
            pathname: location.pathname,
            search: searchParams.toString(),
        });
    }, [location.search, location.pathname, history]);

    const updateFilter = <K extends keyof ListFilters>(
        field: K,
        value: ListFilters[K],
    ) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const resetFilters = () => {
        setFilters(DEFAULT_FILTERS);
    };

    // null when the user has cleared the date field
    const fraDato =
        filters.fromDate === null
            ? null
            : filters.fromDate
              ? new Date(filters.fromDate)
              : getDefaultFromDate();

    const setFraDato = (date: Date | null) => {
        if (!date) {
            updateFilter('fromDate', null);
            return;
        }

        const formattedDate = formatDate(date);
        if (formattedDate) updateFilter('fromDate', formattedDate);
    };

    return {
        filters,
        updateFilter,
        resetFilters,
        fraDato,
        setFraDato,
        setOppslag: (oppslag: string) => updateFilter('oppslag', oppslag),
        tildeltMeg: !!filters.tildeltMeg,
        setTildeltMeg: (tildeltMeg: boolean) =>
            updateFilter('tildeltMeg', tildeltMeg),
        // A search is in use, so the list isn't filtered
        sokPaagaar: !!filters.oppslag?.trim(),
        // «Tildelt meg» is on or the search field has text, so the filters
        // are disabled
        filtreLaast: !!filters.tildeltMeg || !!sokTekst.trim(),
        setSokTekst,
    };
};
